<template>
  <v-autocomplete
    v-if="projectList.length > 5"
    :items="projectList"
    @change="handleSelectChild"
    :value="value"
    item-value="value"
    item-text="text"
    :multiple="multiple"
    :label="label"
    hide-details
    v-bind="$attrs"
    :filter="searchComparator"
  >
    <template v-slot:prepend-item>
      <v-list-item
        v-if="multiple && showSelectAll"
        link
        color="primary"
        :input-value="isAllSelected"
        @click.capture.stop="onToggleAll(!isAllSelected)"
      >
        <v-list-item-action>
          <v-simple-checkbox
            v-model="isAllSelected"
            color="primary"
          />
        </v-list-item-action>
        <v-list-item-content>
          {{ toggleAllLabel }}
        </v-list-item-content>
      </v-list-item>
    </template>
    <template v-slot:selection="{ item: chipItem, index }" v-if="multiple">
      <div v-if="multiple">
        <div :style="multipleStyle" class="v-select__selection v-select__selection--comma" v-if="index < chipCount">{{ chipItem.text }}{{ index < value.length - 1 ? ',' : '' }} &nbsp;</div>
        <v-spacer />
        <div
          class="caption float-right"
          v-if="index === chipCount && value?.length > chipCount"
        >(+{{ (value || []).length - chipCount }} others)
        </div>
      </div>
      <span v-if="!multiple">{{ chipItem?.text }}</span>
    </template>
  </v-autocomplete>
  <v-select
    v-else
    :items="projectList"
    @change="handleSelectChild"
    :value="value"
    item-value="value"
    item-text="text"
    :multiple="multiple"
    label="Project"
    hide-details
    v-bind="$attrs"
  >
    <template v-slot:prepend-item>
      <v-list-item
        v-if="multiple && showSelectAll"
        link
        color="primary"
        :input-value="isAllSelected"
        @click="onToggleAll(!isAllSelected)"
      >
        <v-list-item-action>
          <v-simple-checkbox
            v-model="isAllSelected"
            color="primary"
          />
        </v-list-item-action>
        <v-list-item-content>
          {{ toggleAllLabel }}
        </v-list-item-content>
      </v-list-item>
    </template>
    <template v-slot:selection="{ item: chipItem, index }">
      <div v-if="multiple">
        <div :style="multipleStyle" class="v-select__selection v-select__selection--comma" v-if="index < chipCount">{{ chipItem.text }}{{ index < value.length - 1 ? ',' : '' }} &nbsp;</div>
        <div
          class="caption float-right"
          v-if="index === chipCount && value?.length > chipCount"
        >(+{{ (value || []).length - chipCount }} others)
        </div>
      </div>
      <span v-if="!multiple">{{ chipItem?.text }}</span>
    </template>
    <template v-slot:item="{ item, on }">
      <v-list-item-content>
        <v-list-item-title class="ml-4">{{ item.text }}</v-list-item-title>
      </v-list-item-content>
    </template>
  </v-select>
</template>

<script>
  import { find, map } from 'lodash'

  export default {
    name: 'SelectProject',
    components: {},
    props: {
      value: {
        type: [ String, Number, Array ],
        require: true,
      },
      multiple: {
        type: Boolean,
        default: false,
      },
      label: {
        type: String,
        default: 'Project',
      },
      rules: {
        type: Array,
        require: false,
      },
      projectListCustom: {
        type: Array,
        require: false,
      },
      chipCount: {
        type: Number,
        default: 2,
      },
      defaultValue: {
        type: Boolean,
        default: true
      },
      multipleStyle: {
        type: [ String, Array, Object ],
        default: () => ({
          'max-width': '180px',
        }),
      },
      showSelectAll: Boolean,
    },
    data() {
      return {
        isAllSelected: false,
      }
    },
    computed: {
      projectEntities () {
        return this.projectListCustom || map(this.$store.state.auth.entities, (i) => ({ text: i.name, value: i.entity_id, licence: i.licence }))
      },
      projectList () {
        const otherLicenceName = 'Others'
        const licenceList = [ ...new Set(map(this.projectEntities, 'licence')) ]
        const sortedArray = this.projectEntities.sort((a, b) => {
          if (a.value) {
            return 0
          }
          const licenceA = a.licence || otherLicenceName
          const licenceB = b.licence || otherLicenceName
          return licenceA.localeCompare(licenceB)
        })

        const result = []
        let currentLicence = null

        for (const item of sortedArray) {
          const licence = item.licence || otherLicenceName
          if (licence !== currentLicence) {
            currentLicence = licence
            if (licenceList.length > 1 && `${item.text}`.toLowerCase() !== 'all') {
              result.push({ header: currentLicence })
            }
          }
          result.push(item)
        }

        return result
      },
      isEmptyValue() {
        return Array.isArray(this.value) ? this.value.length === 0 : !this.value
      },
      toggleAllLabel() {
        return this.isAllSelected ? 'Clear' : 'Select All'
      },
    },
    methods: {
      handleSelectChild (item) {
        this.$emit('input', item)
      },
      setDefaultValue () {
        if (this.defaultValue && this.isEmptyValue) {
          const defaultValue = find(this.projectEntities, o => !!o.value)?.value
          this.handleSelectChild(this?.multiple ? [ defaultValue ] : defaultValue)
        }
      },
      searchComparator (item, queryText) {
        if (item.header) {
          return false
        }

        const normalizedQuery = queryText.toString().toLowerCase()

        return item.text.toLowerCase().includes(normalizedQuery)
      },
      onToggleAll(isSelect) {
        this.isAllSelected = isSelect
        this.$emit('input', isSelect ? this.projectEntities.map(entity => entity.value) : [])
      }
    },
    watch: {
      projectListCustom: {
        handler () {
          this.setDefaultValue()
        },
        deep: true,
        immediate: true
      }
    },
    mounted () {
      this.setDefaultValue()

      if (this.multiple && this.showSelectAll) {
        this.$watch('value', (newValue) => {
          this.isAllSelected = newValue.length === this.projectEntities.length
        }, { deep: true, immediate: true })
      }
    },
  }
</script>
<style scoped lang="scss">
  ::v-deep .v-subheader {
    -webkit-user-select: none; /* Chrome/Safari */
    -moz-user-select: none; /* Firefox */
    -ms-user-select: none; /* IE/Edge */
    user-select: none; /* Стандарт */
    //font-weight: bold;
    //font-size: 18px;
  }

  ::v-deep .v-select__selections {
    gap: 4px;
  }

  ::v-deep .v-list-item {
    min-height: 48px;

    &__content {
      padding: 12px 0;
    }
  }

  ::v-deep.v-autocomplete.v-select.v-input--is-focused input {
    //min-width: 24px;
    //padding-top: 2px;
  }

</style>
